export default ngModule => {

  /**
   * Apply validators defined in an object to a field
   *
   * See backend fund handlers for examples of what is returned, eg.
   * {
   *  fieldName: {
   *    pattern: "[A-Za-z]*",
   *    required: true,
   *    minlength: 5,
   *    error_messages: {
   *      pattern: "Please enter only alphabetic characters"
   *    }
   *   }
   * }
   *
   * Adds a custom validator that does all the necessary validation. We don't use
   * the built in angular ones as I couldn't get it to work dynamically adding
   * attributes (eg. adding ng-pattern for example).
   *
   * This also allows easy custom messages
   */
  ngModule.directive('applyValidators', ($parse, $log) => {
    return {
      restrict: 'A',
      require: 'ngModel',
      link: function compile(scope, element, attrs, ctrl) {
        const validators = scope.$eval(attrs.applyValidators);

        if (!validators) {
          $log.error("Expected validators object but got nothing", attrs);
          return;
        }

        element.data('validation', validators);
        if (validators.error_messages) {
          element.data('validation-messages', validators.error_messages);
        }

        const addValidator = (fieldName, fn) => {
          const validator = viewValue => {
            const isValid = fn(viewValue);
            ctrl.$setValidity(fieldName, isValid);
            return isValid ? viewValue : undefined;
          };
          ctrl.$parsers.push(validator);
          ctrl.$formatters.push(validator);
        };

        // Required
        function validateRequired(viewValue) {
          return !validators.required || !ctrl.$isEmpty(viewValue);
        }
        addValidator('required', validateRequired);

        // Regex
        if (validators.pattern) {
          const regexp = new RegExp('^'+validators.pattern+'$');
          function validateRegex(value) {
              return ctrl.$isEmpty(value) || _.isUndefined(regexp) || regexp.test(value);
          }
          addValidator('pattern', validateRegex);
        }

        // Max Length
        if (validators.maxlength) {
          const maxlength = validators.maxlength;
          function validateMaxlength(viewValue) {
            return (maxlength < 0) || ctrl.$isEmpty(viewValue) || (viewValue.toString().length <= maxlength);
          }
          addValidator('maxlength', validateMaxlength);
        }

        // Min Length
        if (validators.minlength) {
          const minlength = validators.minlength;
          function validateMinlength(viewValue) {
            return ctrl.$isEmpty(viewValue) || viewValue.toString().length >= minlength;
          }
          addValidator('minlength', validateMinlength);
        }

        // Medicare number
        // Medicare card number is 10 digits.
        // Rule for Medicare Card validation:
        // 1. For each number starting from the 1st digit to the 8th digit, multiply that number with
        //  a multiplier as specified below in the table and gets the total.
        // 2. Divide the total by 10 and get the remainder.
        // 3. If the remainder is equal to the 9th digit of the Medicare Number and the 1st digit of
        // the Medicare Number is between 2 and 6, then the Medicare Number is valid.
        if (validators.medicare_number) {
          function validateMedicare(viewValue) {
            const n = ctrl.$isEmpty(viewValue) ? "" : viewValue.replace(/[^\d]/, "");
            if (!validators.required && n.length === 0) {
              return true;
            }

            if (n.length !== 10) {
              return false;
            }

            const multipliers = [1,3,7,9,1,3,7,9],
                  check_digit = _.reduce(multipliers, (total, mult, index) => total + parseInt(n[index]) * mult, 0) % 10,
                  digit_1 = parseInt(n[0]);

            return check_digit === parseInt(n[8]) && digit_1 >= 2 && digit_1 <= 6;
          }
          addValidator('medicare_number', validateMedicare);
        }
      }
    }
  });

  /**
   * This is just a helper so you don't need to duplicate template content. eg. Personal details are repeated for
   * partner and main contact. Same for contact details etc. Split it out into a partial and load it with
   * <field-group template="hcf/contact.html" model="ctrl.application.contact" fieldset="ctrl.fieldsets.personal>
   * </field-group>
   */
  ngModule.directive('fieldGroup', ($http, $compile) => {
      return {
        restrict: 'EA',
        require: '^form',
        scope: {
          'template': '@',
          'model': '=',
          'fieldset': '=',
          'prefix': '@',
          'userData': '@',
          'dependantType': '@',
          'onPrepopulate': '=',
          'modelPath': '@',
          // allow caller to pass through a controller
          'ctrl': '=',
        },
        templateUrl: (tElement, tAttrs) => {
          return '/static/partials/'+tAttrs.template;
        },
        link: (scope, element, attrs, ngModel) => {
          scope.prefix = attrs.prefix || attrs.model;
          // give elements access to the model
          scope.modelVal = ngModel;
        }
      }
    }
  );

  /**
   * Angular 1.2 seems to have issues with using expressions in a form name attribute. This directive resolves this,
   *
   * eg. <input dynamic-name="{{prefix}}first_name" />
   */
  ngModule.directive('dynamicName', ($compile, $interpolate) => {
    return {
      restrict: 'A',
      terminal: true,
      priority: 1000000,
      link: (scope, element, attrs) => {
        const name = $interpolate(attrs.dynamicName)(scope);
        element.removeAttr('dynamic-name').attr('name', name);
        $compile(element)(scope);
      }
    }
  });
  
  /**
   * Like dynamicName, but for ng-form attribute interpolation
   */
  ngModule.directive('ngformNameInterpol', ($compile, $interpolate) => {
    return {
      restrict: 'A',
      terminal: true,
      priority: 1000000,
      link: (scope, element, attrs) => {
        const name = $interpolate(attrs.ngformNameInterpol)(scope);
        element.removeAttr('ngform-name-interpol').attr('ng-form', name);
        $compile(element)(scope);
      }
    }
  });
  
  ngModule.directive('focusInvalid', ($timeout, smoothScroll) => {
    return {
      restrict: 'A',
      require: 'form',
      link: (scope, element, attrs, form) => {
        form.focusInvalid = () => {
          $timeout(() => {
            const hasErrorEl = element.find('.has-error input, .has-error select').get(0);
            if (hasErrorEl) {
              smoothScroll(hasErrorEl, {callbackAfter: () => hasErrorEl.focus(), offset: 50});
            }
            const invalidEl = element.find('input.ng-invalid , select.ng-invalid').get(0);
            if (invalidEl) {
              smoothScroll(invalidEl, {callbackAfter: () => invalidEl.focus(), offset: 50});
            }
          });
        }
      }
    }
  });


  ngModule.directive('buttonLoadingIndicator', () => {
    return {
      restrict: 'A',
      transclude: true,
      scope: {
        buttonLoadingIndicator: '='
      },
      template: `
        <span ng-show="buttonLoadingIndicator"><i class="fa fa-spinner fa-spin"></i> Please wait...</span>
        <span ng-show="!buttonLoadingIndicator" ng-transclude></span>
      `,
      link: (scope, element, attrs, form) => {
        scope.$watch('buttonLoadingIndicator', isLoading => {
          if (isLoading) {
            element.attr('disabled', true);
            element.addClass('btn--disabled');
          } else {
            element.removeAttr('disabled');
            element.removeClass('btn--disabled');
          }
        })
      }
    }
  });

  ngModule.directive('iframeOnload', () => {
    return {
      scope: {
        callBack: '&iframeOnload'
      },
      link: function (scope, element, attrs) {
        element.on('load', function () {
          return scope.callBack();
        });
      }
    }
  });


  /**
   * This is used to fix-up tooltip box positioning when they would otherwise overflow the left
   * viewport edge.
   */
  ngModule.directive('positionTooltip', ['$timeout', function ($timeout) {
    return {
      restrict: 'A',
      scope: false,
      link: function (scope, element, attrs) {
        let el, arrow;
        element.bind('mouseenter', function () {
          $timeout(function () {
            el = element.next();
            if (el.length && el[0].getBoundingClientRect().left < 0) {
              el.addClass('reposition-left');
            }
            if (el.length && el[0].getBoundingClientRect().right >
              (window.innerWidth || document.documentElement.clientWidth)) {
              el.addClass('reposition-right');
            }
            // Reposition the arrow location
            const iRect = element[0].getBoundingClientRect();
            const iRectCenter = iRect.left + ((iRect.right - iRect.left) / 2);
            const tooltipRect = el[0].getBoundingClientRect();
            let position = iRectCenter - tooltipRect.left;
            arrow = el.find('.tooltip-arrow').css('left', position + 'px');
          })
        });
      }
    }
  }]);

  /**
   * Element that provides progressive lookup on suburb names and postcodes
   */
  ngModule.directive('suburbLookup', function(AonApi, $timeout, $http) {
    return {
      restrict: 'E',
      scope: {
        model: '=',
        state: '=',
      },
      template: require('./suburb.html'),
      link: function (scope, element, attrs) {
        const titleCase = str => str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
        scope.displayResult = (result, type) => {
          if (typeof result === 'string') return result;
          if (result) return `${result.suburb} ${result.postcode}`;
          return scope.model[type];
        };
        scope.resultSelect = suburb => {
          scope.model.suburb = titleCase(suburb.suburb);
          scope.model.postcode = suburb.postcode;
        };
        scope.suburbLookup = (which, value) => {
          scope.loading = true;
          scope.noResults = false;
          const state_filter = scope.state ? `&state=${scope.state}` : '';
          const endpoint = `/api/suburb?${which}=${value}` + state_filter;
          return $http.get(endpoint).then(response => {
            scope.loading = false;
            scope.noResults = response.data.length == 0;
            return response.data;
          });
        };
      },
    };
  });

  /**
   * Get extra product information based on display location/placement
   */
  ngModule.directive('productExtraInformation', function (AonApi, AonConfig, $timeout, $http) {
    return {
      restrict: 'E',
      scope: {
        package: '=',
        placement: '@',
      },
      template: `<i ng-if="info" tooltip-html-unsafe="{{ info }}" position-tooltip class="glyphicon glyphicon-info-sign"></i>`,
      link: function (scope) {
        const pkg = scope['package'];
        const placement = scope.placement;
        const placement_code = AonConfig.jsConstants.ProductExtraInformation.placement[placement];

        let list = _.get(pkg, 'hospital_product.product_information', [])
            .concat(_.get(pkg, 'ancillary_product.product_information', []))
            .filter(x => x.information_placement == placement_code);

        if (!list.length) return null;

        // Get unique entries (lodash function for this is in 4.0.0, we are on 3.7.ish, 4.0.0 not compatible?)
        let seenIDs = [];
        let uniqueList = [];
        for (let l of list) {
          if (!_.includes(seenIDs, l.id)) {
            seenIDs.push(l.id);
            uniqueList.push(l);
          }
        }
        scope.info = uniqueList.map(x => `${x.information_type_label}: ${x.information}`).join('<br>');
      }
    };
  });


  /**
   * Element that provides progressive lookup on health cover products, e.g.
   * for selecting a current health cover product.
   */
  ngModule.directive('productLookup', function(AonApi, AonConfig, $timeout, $http) {
    return {
      // Access the form controller in this directive
      require: '^form',
      restrict: 'E',
      scope: {
        model: '=',
        fund: '@',
        state: '@',
        membership: '@',
        corporateSearch: '=',
      },
      template: require('./product-lookup.html'),
      link: function (scope, element, attrs, form) {
        scope.formChoices = AonConfig.formChoices;
        scope.isBrokerCorporate = (
          AonConfig.corporate.distribution_type === AonConfig.jsConstants.DistributionTypeLookup.BROKER
        );
        const corporateId = AonConfig.corporate.id;
        const {HOSPITAL, ANCILLARY, BOTH} = AonConfig.jsConstants.ProductPriceType;

        if (scope.corporateSearch) {
          scope.prefix = 'comparison';
          scope.hospitalLabel = 'Comparison Hosp/Combined Cover';
          scope.ancillaryLabel = 'Comparison Extras Cover';
        } else {
          scope.prefix =  'current';
          scope.hospitalLabel = 'Current Hosp/Combined Cover';
          scope.ancillaryLabel = 'Current Extras Cover';
        }

        const getParams = (name = null, which = null) => {
          const { fund } = scope;
          const { state = scope.state, membership = scope.membership } = scope.model;
          const params = { state, membership, corporate: corporateId };
          if (name) {
            params.name = name;
          }
          if (which) {
            const policy = which === 'ancillary' ? ANCILLARY : [HOSPITAL, BOTH];
            params.policy = policy;
          }
          if (fund) {
            params.fund = fund;
          }
          return params;
        };

        scope.validators = {
          'error_messages': {
            'product': 'At least one product must be selected',
            'editable': 'Please select a valid product',
          },
        };

        scope.fund_monthly_payment_validator = {

          'error_messages': {
            'range': 'Please enter the price from 0 to 999.99',
            'premium': "This field is required"
          },

        };

        const checkValidity = () => {
          if (form[`${scope.prefix}_hospital_product`]) {
            const {hospital_product, ancillary_product} = scope.model;
            if (!hospital_product && !ancillary_product) {
              form[`${scope.prefix}_hospital_product`].$setValidity('product', false);
              form[`${scope.prefix}_ancillary_product`].$setValidity('product', false);
              form[`${scope.prefix}_hospital_product`].$pristine = false;
              form[`${scope.prefix}_ancillary_product`].$pristine = false;
            } else {
              form[`${scope.prefix}_hospital_product`].$setValidity('product', true);
              form[`${scope.prefix}_ancillary_product`].$setValidity('product', true);
            }
          }
        };

        scope.$watch('model.hospital_product', checkValidity);
        scope.$watch('model.ancillary_product', checkValidity);

        scope.loading = {
          hospital: false,
          ancillary: false,
        };

        scope.showProductLookup = () => {
          const isSet = val => {
            const notEmpty = val !== null && val !== undefined;
            if (typeof val === 'string') {
              return val !== '' && notEmpty;
            } else {
              return notEmpty;
            }
          }
          const { fund, state, membership } = getParams();
          return (isSet(fund) || scope.corporateSearch) && isSet(state) && isSet(membership);
        };

        scope.showAncillaryLookup = () => {
          const hasAncillary = scope.model.hospital_product && scope.model.hospital_product.type === BOTH;
          return !hasAncillary;
        };

        scope.selectHospital = item => {
          scope.model.hospital_product = item;
          if (item.type === BOTH) {
            scope.model.ancillary_product = null;
          }
        };

        scope.selectAncillary = item => {
          scope.model.ancillary_product = item;
        };

        scope.displayResult = (result) => {
          if (typeof result === 'string') return result;
          if (result) {
            let name = result.name;
            if (result.monthly_premium) {
              name += ` (Premium: $${result.monthly_premium}, Excess: $${Math.round(result.excess)}, ${result.product_status})`;
            }
            return name;
          }
        };

        scope.productLookup = (name, which) => {
          scope.loading.hospital = false;
          scope.loading.ancillary = false;
          scope.loading[which] = true;
          const endpoint = '/api/previous-fund-products/';
          const params = getParams(name, which);
          return $http.get(endpoint, {params}).then(response => {
            scope.loading[which] = false;
            return response.data.concat({
              name: 'Other (no matching product found)',
              type: which === 'ancillary' ? ANCILLARY : HOSPITAL,
              excess: 0,
            });
          });
        };

        scope.clearCurrentHealthFundProducts = () => {
          scope.model.hospital_product = null;
          scope.model.ancillary_product = null;
        };

        scope.$watch('model.fund_id', (newVal, oldVal) => {
          if (!oldVal && !newVal && scope.corporateSearch) {
            return;
          }
          if (!newVal || !oldVal || (newVal && oldVal && newVal !== oldVal)) {
            scope.clearCurrentHealthFundProducts();
          }
        });
      },
    };
  });

  /**
   * Element that provides an input for entering LHC age either as an integer 30-110
   * or as a percentage, an even number 2%, 4%, 8%, between 0% and 70%
   */
  ngModule.directive('lhcAgeEntry', function() {
    return {
      // We need to access both the form controller and the ngmodel controller
      require: ['^form', 'ngModel'],
      restrict: 'A',
      scope: {
        model: '=ngModel',
        name: '@',
      },
      link: function (scope, element, attrs, [form, model]) {
        const checkValidity = () => {
          if (form[scope.name]) {
            const sanitizedValue = String(scope.model || '').replace(/[^\d%]/, '');
            model.$setViewValue(sanitizedValue);
            model.$render();

            const percentageInput = String(scope.model).match(/^(\d+)%$/);
            const numberInput = String(scope.model).match(/^(\d+)$/);
            if (percentageInput) {
              const percent = percentageInput[1];
              let valid = true;
              if (percent % 2 || percent < 0 || percent > 70) {
                valid = false;
              } else {
                model.$setViewValue(30 + (parseInt(percent) / 2));
                model.$render();
              }
              form[scope.name].$setValidity('number', true);
              form[scope.name].$setValidity('percentage', valid);
            } else if (numberInput) {
              const number = numberInput[1];
              let valid = true;
              if (number < 30 || number > 110) {
                valid = false;
              }
              form[scope.name].$setValidity('percentage', true);
              form[scope.name].$setValidity('number', valid);
            } else {
              form[scope.name].$setValidity('number', false);
            }
          }
        };

        scope.$watch('model', checkValidity);
      },
    };
  });

  /**
   * Element that provides an input for Monthly amount you currently pay
   * range from 0 - 999 and is required
   */

  ngModule.directive('currentMonthlyPremium', function() {
    return {
      // We need to access both the form controller and the ngmodel controller
      require: ['^form', 'ngModel'],
      restrict: 'A',
      scope: {
        model: '=ngModel',
        name: '@',
      },
      link: function (scope, element, attrs, [form, model]) {

        const checkValidity = () => {

          if (form[scope.name]) {

            if (scope.model !== null && scope.model !== undefined) {

              if (scope.model >= 0 && scope.model <= 999.99) {

                form[scope.name].$setValidity('range', true);

              } else {

                form[scope.name].$setValidity('range', false);

              }

              form[scope.name].$setValidity('premium', true);

            } else {

              form[scope.name].$setValidity('premium', false);

            }

          }

        }

        scope.$watch('model', checkValidity)

      }
    };
  });

  ngModule.directive('radioOptions', function () {
    return {
      restrict: 'E',
      scope: {
        model: '=',
        name: '@',
        options: '=?',
        onChange: '=?',
      },
      template: `
      <div class="radio-group">
        <label class="radio-inline" ng-repeat="option in options" >
            <input
                type="radio"
                dynamic-name="{{ $parent.name }}"
                ng-model="$parent.model"
                ng-value="option[1]"
                required
                ng-change="onChange"
            /> {{ option[0] }}
        </label>        
      </div>
      `,
      link: function ($scope) {
        if (!$scope.options) {
          $scope.options = [
              ['Yes', true],
              ['No', false],
          ];
        }
      },
    };
  })
  
}



// WEBPACK FOOTER //
// ./src/directives/form-directives.js